import { Loader2 } from 'lucide-react';
import { $generateHtmlFromNodes, RichTextEditor } from '@talentmesh/rte';
import React, { useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Button } from '@/Components/shadcn/button';
import { TypographyP } from '@/Components/shadcn/typography';
import UIStrings from '@/Utils/UIStrings';
import { cn } from '@/lib/utils';
import { CandidatesNoteForm } from './noteValidationScheme';

interface NoteRTEProps {
    saved: boolean;
    errorSaving: boolean;
}

function NoteRTE({ saved, errorSaving }: NoteRTEProps): React.JSX.Element {
    const {
        formState: { isDirty, isSubmitting },
        setValue,
        control,
    } = useFormContext<CandidatesNoteForm>();
    const {
        field,
        fieldState: { error },
    } = useController({ control, name: 'noteRte' });
    const [isInitialRender, setIsInitialRender] = useState(true);
    const initialHtml = useMemo(() => field.value, []);

    return (
        <div className="flex flex-col items-start gap-4" data-cy="NoteSection">
            <div
                className={cn(
                    'flex h-120 w-full flex-col rounded-xl border',
                    error?.message ? 'border-destructive' : 'border-border',
                )}
            >
                <RichTextEditor
                    data-cy="NoteEditor"
                    initialHtml={initialHtml}
                    features={['headingStyle', 'bold', 'italic', 'ol', 'ul']}
                    onChange={(editorState, editor) => {
                        editorState.read(() => {
                            setValue('noteRte', $generateHtmlFromNodes(editor), { shouldValidate: !isInitialRender });
                            if (isInitialRender) {
                                setIsInitialRender(false);
                            }
                        });
                    }}
                    contentEditableProps={{
                        className: 'flex-1 overflow-y-auto',
                    }}
                />
                {error?.message && <TypographyP className="text-destructive">{error.message}</TypographyP>}
            </div>
            <div className="flex flex-row items-center gap-5">
                <Button data-cy="NoteSubmitButton" size="lg" disabled={isSubmitting} type="submit">
                    {isSubmitting && <Loader2 className="size-4 animate-spin" aria-hidden />}
                    {UIStrings.Save}
                </Button>
                {isSubmitting && <TypographyP>{UIStrings.SavingWithDots}</TypographyP>}
                {saved && !isDirty && <TypographyP>{UIStrings.Saved}</TypographyP>}
                {errorSaving && <TypographyP className="text-destructive">{UIStrings.NoteCannotBeSaved}</TypographyP>}
            </div>
        </div>
    );
}

export default NoteRTE;
